/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelMarketInsightChartsChartSolidgaugeLegend from 'lumen/components/hotel/market-insight/charts/chart-solidgauge-legend';
import { stackGaugeChartData } from 'lumen/utils/charts/chart-utils';
import {
  GAUGE_INNER_RADIUS,
  GAUGE_OUTER_RADIUS,
} from 'lumen/utils/constants/market-insight-constants';

interface HotelMarketInsightChartsChartSearchPatternsLosGenericSignature {
  Args: {
    currentExtractDate: unknown;
    onUpdateChartReference: unknown;
  };
}

export default class HotelMarketInsightChartsChartSearchPatternsLosGeneric extends Component<HotelMarketInsightChartsChartSearchPatternsLosGenericSignature> {
  @service declare intl: Services['intl'];
  @service declare miSearches: Services['mi-searches'];

  // Mapping of LOS series to colors in the chart
  losColorMapping = {
    LOS1: 'var(--prism-color-chart-categorical-01)',
    LOS2: 'var(--prism-color-chart-categorical-02)',
    LOS3: 'var(--prism-color-chart-categorical-03)',
    'LOS4-7': 'var(--prism-color-chart-categorical-04)',
    'LOS8-14': 'var(--prism-color-chart-categorical-05)',
  };

  // Mapping to order the legend correctly (will be sorted DESC)
  losSeriesLegendOrderMapping = {
    LOS1: 9,
    LOS2: 8,
    LOS3: 7,
    'LOS4-7': 6,
    'LOS8-14': 5,
  };
  // Mapping to order the stacks correctly in the chart (higher number means lower in the stack)
  losSeriesOrderMapping = {
    LOS1: 5,
    LOS2: 6,
    LOS3: 7,
    'LOS4-7': 8,
    'LOS8-14': 9,
  };

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.dayModel.date',
    'args.{currentExtractDate,searchesType}',
    'losColorMapping',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    // @ts-expect-error: Incorrect type
    const chartData = [];

    // object to store all LOS search objects for the correct extract date grouped per LOS type
    const losSearchObjectsForCurrentExtractDatePerLos = {};

    // Get all searchsummary objects for the days in this range
    if (this.args.currentExtractDate) {
      // Get the LOS search objects for the correct extract date
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const losSearchObjectsFilteredByType =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.miSearches.filterLosSearchesByExtractDate(
          // @ts-expect-error: Incorrect type
          this.args.searchesType,
          // @ts-expect-error: Incorrect type
          this.args.dayModel,
          this.args.currentExtractDate,
        );
      // Go over all LOS search objects and store them by "LOS type" (e.g.: LOS1, LOS2, LOS3, LOS4-7, LOS8-)
      // That are the different "stacks" we need to show per day column in the chart
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      losSearchObjectsFilteredByType.forEach((losSearchObject) => {
        if (
          // @ts-expect-error: Incorrect type
          !losSearchObjectsForCurrentExtractDatePerLos[
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            losSearchObject.losRangeFormatted
          ]
        ) {
          // @ts-expect-error: Incorrect type
          losSearchObjectsForCurrentExtractDatePerLos[
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            losSearchObject.losRangeFormatted
          ] = [];
        }

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        losSearchObjectsForCurrentExtractDatePerLos[
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          losSearchObject.losRangeFormatted
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        ].push(losSearchObject);
      });
    }

    Object.keys(losSearchObjectsForCurrentExtractDatePerLos).forEach((key) => {
      if (key !== 'staythrough') {
        // Rounding the values to one decimal place
        const roundedValue =
          Math.round(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            losSearchObjectsForCurrentExtractDatePerLos[key][0]
              .searchFractionArrivalAsPercentage * 10,
          ) / 10;

        const dataForCurrentLos = {
          name: key,
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          color: this.losColorMapping[key],
          originalY: roundedValue,
          y: roundedValue,
        };

        chartData.push(dataForCurrentLos);
      }
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    chartData.sort((a, b) => b.name - a.name);

    return [
      {
        name: 'most-searched-los',
        className: 'gauge-chart-border',
        innerRadius: GAUGE_INNER_RADIUS,
        radius: GAUGE_OUTER_RADIUS,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        data: stackGaugeChartData(chartData),
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    return {
      chart: {
        type: 'solidgauge',
        height: 90,
        spacingTop: 0,
        spacingRight: 0,
        spacingBottom: 0,
        spacingLeft: 0,
        animation: false,
      },
      title: {
        text: '',
      },
      subtitle: {
        text: `<div class="text-dark flex flex-col">
                <h3 class="flex justify-center">${this.formattedMostSearchedLosLabel}</h3>
                <p class="text-200--regular" style="text-align: center;">${this.intl.t(
                  'market_insight.search_patterns.most_searched_los',
                )}</p>
              </div>`,
        useHTML: true,
        align: 'center',
        verticalAlign: 'middle',
        y: 40,
      },
      pane: {
        center: ['50%', '100%'],
        size: '190%',
        startAngle: -90,
        endAngle: 90,
        background: [
          {
            shape: 'arc',
            innerRadius: GAUGE_INNER_RADIUS,
            outerRadius: GAUGE_OUTER_RADIUS,
            backgroundColor: 'var(--prism-color-chart-neutral-figure-empty)',
            borderWidth: 0,
          },
        ],
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
      },
      yAxis: {
        min: 0,
        max: 100,
        lineWidth: 0,
        tickPositions: [],
      },
      plotOptions: {
        solidgauge: {
          animation: false,
          dataLabels: {
            enabled: false,
          },
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMostSearchedLosLabel() {
    // @ts-expect-error: Incorrect type
    if (this.chartData[0].data.length) {
      // @ts-expect-error: Incorrect type
      if (this.chartData[0].data.some((los) => los.originalY > 0)) {
        // @ts-expect-error: Incorrect type
        const maxLosValueObject = this.chartData[0].data.reduce(
          (previousLos, currentLos) =>
            // @ts-expect-error: Incorrect type
            previousLos.originalY > currentLos.originalY
              ? previousLos
              : currentLos,
        );

        return maxLosValueObject.name.replace('LOS', '');
      }
      return this.intl.t('general.missing');
    }

    return '';
  }

  @computed('chartData.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get legendInfo() {
    // Data points are in reversed order for the gauge chart stacking - reverse back for the legend
    // @ts-expect-error: Incorrect type
    return [...this.chartData[0].data].reverse().map((datapoint) => ({
      name: datapoint.name,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value: datapoint.originalY,
      color: datapoint.color,
    }));
  }

  <template>
    <div class="flex flex-row items-center">
      <div class="chart-solidgauge-wrapper">
        <GeneralChartsOtaChart
          {{! @glint-expect-error: Incorrect type }}
          @chartOptions={{this.chartOptions}}
          @model={{this.chartData}}
          {{! @glint-expect-error: Incorrect type }}
          @onUpdateChartReference={{@onUpdateChartReference}}
        />
      </div>
      <HotelMarketInsightChartsChartSolidgaugeLegend
        @legendInfo={{this.legendInfo}}
      />
    </div>
  </template>
}
